import { LitElement, html, render, TemplateResult } from 'lit';
import { Policy } from '../../../policiesSelector/types';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { GzTableWebComponent } from '../../../ui/gzTable';
import { PoliciesSelectorWebComponent } from '../../../policiesSelector';
import { Dependencies, EditPolicyAssignmentEvents, PoliciesSelector } from '../types';
import { styles } from '../view/styles';
import { customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_EDIT_POLICY_ASSIGNMENT_PANEL } from '../../../webcomponents.constants';
import { htmlDecode } from '../../../utils/escape.utils';
import { translateFn } from '../../../utils/translator.helper';
import { Rights } from '@nirvana/shared/constants';
import { GzTextWebComponent } from '../../../ui/gzText';

@customElement(WEB_COMPONENT_EDIT_POLICY_ASSIGNMENT_PANEL)
export class EditPolicyAssignmentPanelWebComponent extends LitElement {
  private readonly dependencies: Dependencies;
  private bodyContentHtml: HTMLElement;
  private footerContentHtml: HTMLElement;
  private impactTable: HTMLElement;
  private impactSection: HTMLElement;
  private dialog: GzDialogWebComponent;
  private readonly policyIdsDisplayedOnlyInPreview: Array<string> = [];

  public columnPolicyName = "";
  public columnInheritsFrom = "";
  public panelTitle = "";
  public previewTitle = "";
  public previewMessage = "";
  public policiesSelectorLabel = "";
  public policiesSelectorProps: PoliciesSelector = {
    moduleAndSection: {
      moduleId: "",
      sectionId: "",
    },
    excludeInheritedPolicies: true,
    selectedPolicies: [],
  };

  static get styles() {
    return styles;
  }

  constructor(dependencies) {
    super();
    this.dependencies = dependencies;
    this.previewTitle = translateFn('Policies preview');
    this.panelTitle = translateFn('Edit policy assignment');
    this.columnPolicyName = translateFn('Policy name');
    this.columnInheritsFrom = translateFn('Inherits from');
    this.previewMessage = '';

  }

  public displayPanel(parentElement?: HTMLElement) {
    if (parentElement?.querySelector("gz-dialog")) {
      (parentElement?.querySelector("gz-dialog") as GzDialogWebComponent).close();
    }

    this.createDialogTemplates();
    this.loadDataInPolicySelector();
    this.dialog = GzDialogWebComponent.buildDialogElement({
      headline: this.panelTitle,
      bodyContent: this.bodyContentHtml,
      footerContent: this.footerContentHtml,
      isOpen: true,
      isWindow: true,
    }, parentElement);
  }

  private loadDataInPolicySelector() {
    const selector = this.bodyContentHtml.querySelector('gz-policies-selector') as PoliciesSelectorWebComponent;
    const previewPolicies = [...this.policiesSelectorProps.selectedPolicies];

    selector.propertiesSet = false;
    selector.setAttribute('data-selected-target', JSON.stringify(this.policiesSelectorProps.selectedPolicies));
    selector.injectDependencies(this.dependencies);
    selector.start().then(() => {
      // policies that will appear selected in policy selector
      const editableSelectedPolicies: Array<string> = [];

      // if there are any policies previously linked to this list, make sure to display the table containing them
      if (this.policiesSelectorProps.selectedPolicies.length > 0) {

        // add the policies that have inheritance in the policies preview
        for (const policy of selector.policy) {

          // check the inherited policy exists in preview policies list and check that the policy with inheritance don't exist already in the preview list
          if (policy?.inheritsFrom && previewPolicies.indexOf(policy.inheritsFrom) !== -1 && previewPolicies.indexOf(policy.value) === -1) {
            previewPolicies.push(policy.value);
          }

          // add the policies that appear only in Policies Preview section in a separate array
          if (!policy?.isEditable && !policy?.inheritsFrom && previewPolicies.indexOf(policy.value) !== -1) {
            this.policyIdsDisplayedOnlyInPreview.push(policy.value);
          }

          // add policies that are editable into separate array (selectedPolicies may contain policies that are not editable)
          if (policy?.isEditable && !policy?.inheritsFrom && this.policiesSelectorProps.selectedPolicies.indexOf(policy.value) !== -1) {
            editableSelectedPolicies.push(policy.value);
          }
        }
        this.policiesSelectorProps.selectedPolicies = editableSelectedPolicies;
        this.impactSection.appendChild(this.impactTable);
        this.bodyContentHtml.appendChild(this.impactSection);
        this.updateImpactTable(this.impactTable, previewPolicies, selector.policy);
      }
    }).catch(console.error);
  }

  private dialogBodyTemplate(): TemplateResult {
    const disableSaveBtn = !Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE]);

    return html`
      <gz-text fontWeight="400" fontSize="14" lineHeight="1.2" id="policiesSelectorLabel"></gz-text>
      <gz-policies-selector
        class="mt-2 mb-4"
        @selectionChange=${this.policiesSelectorSelectionChangeEvent.bind(this)}
        data-editable-module-and-section=${JSON.stringify(this.policiesSelectorProps.moduleAndSection)}
        data-selected-target="${JSON.stringify(this.policiesSelectorProps.selectedPolicies)}"
        data-exclude-policies='${this.policiesSelectorProps.excludeInheritedPolicies}' ?disabled="${disableSaveBtn}">
      </gz-policies-selector>
      <gz-title class="title mb-2" fontweight="400" fontsize="18">${this.previewTitle}</gz-title>
      <gz-text class="pb-2" fontWeight="400" fontSize="14" lineHeight="1.2" id="previewMessage"></gz-text>
    `;
  }

  private dialogFooterTemplate(): TemplateResult {
    let footerMessageClass;
    let disableSaveBtn;
    if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      footerMessageClass = "";
      disableSaveBtn = true;
    } else {
      footerMessageClass = "hidden";
      disableSaveBtn = false;
    }

    return html`
      <gz-button id='confirm' @click="${this.saveClickEvent.bind(this)}" type='full' class='mr-2' ?disabled="${disableSaveBtn}">${translateFn('Save#preserve state')}</gz-button>
      <gz-text id="footerMessage" class="${footerMessageClass}" fontWeight="400" fontSize="14" style="line-height:32px">${translateFn('Additional rights required.')}</gz-text>
      <gz-button id='cancel' @click="${this.cancelClickEvent.bind(this)}" type='outline' color='secondary'>${translateFn('CANCEL')}</gz-button>
    `;
  }

  private createDialogTemplates() {
    const bodyContentHtml = document.createElement('div');
    bodyContentHtml.classList.add('px-5', 'py-3');
    render(this.dialogBodyTemplate(), bodyContentHtml);

    const policiesSelectorLabel = bodyContentHtml.querySelector("#policiesSelectorLabel") as GzTextWebComponent;
    const previewMessage = bodyContentHtml.querySelector("#previewMessage") as GzTextWebComponent;
    previewMessage.innerHTML = this.previewMessage;
    policiesSelectorLabel.innerHTML = this.policiesSelectorLabel;

    this.impactSection = document.createElement('div');
    this.impactTable = this.createImpactTable();
    this.impactSection.appendChild(this.impactTable);
    bodyContentHtml.appendChild(this.impactSection);

    const footerContentHtml = document.createElement('div');
    render(this.dialogFooterTemplate(), footerContentHtml);

    this.bodyContentHtml = bodyContentHtml;
    this.footerContentHtml = footerContentHtml;
  }

  private policiesSelectorSelectionChangeEvent(e: CustomEvent) {
    const policies: Array<Policy> = JSON.parse((e?.currentTarget as HTMLElement).dataset?.policies || "[]");
    this.policiesSelectorProps.selectedPolicies = e.detail.selectedValues.map(index => {
      return policies[index]['value'];
    });

    const previewPolicies = [...this.policiesSelectorProps.selectedPolicies, ...this.policyIdsDisplayedOnlyInPreview];

    // add the policies that have inheritance in the policies preview
    for (const policy of policies) {
      // check the inherited policy exists in preview policies list and check that the policy with inheritance don't exist already in the preview list
      if (policy?.inheritsFrom && previewPolicies.indexOf(policy.inheritsFrom) !== -1 && previewPolicies.indexOf(policy.value) === -1) {
        previewPolicies.push(policy.value);
      }
    }

    this.updateImpactTable(this.impactTable, previewPolicies, policies);
  }

  private updateImpactTable(table, selectedPolicies: Array<string>, policies: Array<Policy>) {
    table.data = selectedPolicies.map(selectedPolicy => {
      const policy = policies.find(x => x.value === selectedPolicy);
      if (policy) {
        const parentPolicy = policies.find(x => x.value === policy?.inheritsFrom);
        policy['endpointsCount'] = policy['endpointsCount'] ?? 0;
        return {
          label: htmlDecode(policy['label']),
          value: [htmlDecode(parentPolicy?.label ?? "")]
        };
      }
    });
  }

  private createImpactTable(): GzTableWebComponent {
    const table = new GzTableWebComponent();
    table.head = {
      header: [this.columnPolicyName, this.columnInheritsFrom],
      color: [''],
    };
    return table;
  }

  private saveClickEvent(event: CustomEvent) {
    this.dispatchEvent(new CustomEvent(EditPolicyAssignmentEvents.CLICK_ON_SAVE, {
      bubbles: true,
      composed: true,
      detail: {
        selectedPolicies: this.policiesSelectorProps.selectedPolicies,
      },
    }));

    this.dialog.close();
  }

  private cancelClickEvent() {
    this.dialog.close();
  }

}
